/**
 * PV Quiz Viking - Cart
 *
 * The only place that calls /cart/add.js. Reads variant/selling-plan ids
 * from `meta.pvVariantId`/`meta.pvSellingPlanId`, captured at selection time
 * from the real, unmodified pv-product-section instance (q13_product) by
 * pv-quiz-viking-offer-bridge.js / pv-quiz-viking-offer.js - nothing here is
 * hardcoded, so pricing/plan changes in Shopify never go stale. Cart is
 * built in one step, only when the user actually proceeds to checkout -
 * never earlier in the flow. Only exception: the Offer Page's boost add-on
 * card adds its line on click, and checkout keeps that line (keepVariantIds).
 */

(function () {
  'use strict';

  function buildCartItems(meta) {
    if (!meta.pvVariantId) return [];

    var item = {
      id: parseInt(meta.pvVariantId, 10),
      quantity: 1
    };

    if (meta.purchaseType !== 'one_time' && meta.pvSellingPlanId) {
      item.selling_plan = parseInt(meta.pvSellingPlanId, 10);
    }

    return [item];
  }

  function addToCart(items) {
    return fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items: items })
    }).then(function (response) {
      if (!response.ok) {
        return response.json().then(function (body) {
          throw new Error((body && body.description) || 'Cart add failed');
        });
      }
      return response.json();
    });
  }

  // Checkout should reflect only what the quiz just built - never whatever a
  // shopper already had sitting in their cart from an earlier session/page.
  function clearCart() {
    return fetch('/cart/clear.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' }
    }).then(function (response) {
      if (!response.ok) throw new Error('Cart clear failed');
      return response.json();
    });
  }

  // Same as clearCart(), but leaves lines of the given variants untouched
  // (the Offer Page's boost add-on, added to the real cart on click).
  // Resolves to the variant ids actually still in the cart.
  function clearCartExcept(keepVariantIds) {
    if (!keepVariantIds || !keepVariantIds.length) {
      return clearCart().then(function () { return []; });
    }

    var keep = keepVariantIds.map(String);

    return fetch('/cart.js', { headers: { Accept: 'application/json' } })
      .then(function (response) {
        if (!response.ok) throw new Error('Cart read failed');
        return response.json();
      })
      .then(function (cart) {
        var kept = [];
        var updates = {};

        (cart.items || []).forEach(function (line) {
          if (keep.indexOf(String(line.variant_id)) !== -1) {
            kept.push(String(line.variant_id));
          } else {
            updates[line.key] = 0;
          }
        });

        if (!Object.keys(updates).length) return kept;

        return fetch('/cart/update.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ updates: updates })
        }).then(function (response) {
          if (!response.ok) throw new Error('Cart clear failed');
          return kept;
        });
      });
  }

  // Quiz answers as cart attributes (_quiz_goal, ...) so they show on the order.
  function updateCartAttributes(attributes) {
    return fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ attributes: attributes })
    }).then(function (response) {
      if (!response.ok) throw new Error('Cart attributes update failed');
      return response.json();
    });
  }

  function submitCartAndRedirect(items, attributes, keepVariantIds, onError) {
    if (!items.length) {
      if (onError) onError(new Error('No items to add'));
      return;
    }

    clearCartExcept(keepVariantIds)
      .then(function (kept) {
        var toAdd = items.filter(function (item) {
          return kept.indexOf(String(item.id)) === -1;
        });
        return toAdd.length ? addToCart(toAdd) : null;
      })
      .then(function () {
        if (!attributes || !Object.keys(attributes).length) return null;
        // Attributes are nice-to-have - never block checkout on them.
        return updateCartAttributes(attributes).catch(function (err) {
          console.warn('PV Quiz Viking: cart attributes not saved', err);
        });
      })
      .then(function () {
        window.location.href = '/checkout';
      })
      .catch(function (err) {
        if (onError) onError(err);
      });
  }

  window.PVQuizVikingCart = {
    buildCartItems: buildCartItems,
    submitCartAndRedirect: submitCartAndRedirect
  };
})();
